import { useEffect, useState } from "react";
import { Button, Card, Input, Space, Table, Tag, message, Modal, Spin } from "antd";
import { EyeOutlined, FileTextOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { caseAPI, documentAPI } from "../services/api";
import { Case, Document } from "../types";

type DocRow = Document & {
  case_number?: string;
  case_id?: number;
};

const DocumentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<DocRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [ocrVisible, setOcrVisible] = useState(false);
  const [ocrTitle, setOcrTitle] = useState("");
  const [ocrText, setOcrText] = useState("");
  const [ocrLoading, setOcrLoading] = useState(false);

  useEffect(() => {
    void loadDocuments();
  }, []);

  async function loadDocuments() {
    setLoading(true);
    try {
      const cases: Case[] = await caseAPI.getAll();
      const all: DocRow[] = [];
      for (const c of cases) {
        const docs = await documentAPI.getByCaseId(c.case_id);
        for (const d of docs) {
          all.push({
            ...d,
            case_id: c.case_id,
            case_number: c.case_number,
          });
        }
      }
      all.sort((a, b) => {
        const da = a.upload_date ? new Date(a.upload_date).getTime() : 0;
        const db = b.upload_date ? new Date(b.upload_date).getTime() : 0;
        return db - da;
      });
      setRows(all);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || "No se pudieron cargar documentos");
    } finally {
      setLoading(false);
    }
  }

  async function openOcr(record: DocRow) {
    setOcrTitle(record.title || `Documento #${record.document_id}`);
    setOcrVisible(true);
    const cached = (record.content_text || "").trim();
    if (cached) {
      setOcrText(cached);
      setOcrLoading(false);
      return;
    }
    setOcrLoading(true);
    setOcrText("");
    try {
      const text = await documentAPI.getPlainText(record.document_id);
      setOcrText(text || "No hay texto OCR guardado para este documento.");
    } catch {
      setOcrText("Error al cargar OCR.");
    } finally {
      setOcrLoading(false);
    }
  }

  const needle = filter.trim().toLowerCase();
  const filtered = needle
    ? rows.filter((r) =>
        [r.title, r.case_number, r.document_type, r.status, r.content_text]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(needle))
      )
    : rows;

  return (
    <div>
      <Card
        title="Documentos"
        extra={
          <Space>
            <Input.Search
              allowClear
              placeholder="Buscar por título, caso u OCR…"
              style={{ width: 320 }}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
            <Button onClick={() => void loadDocuments()}>Recargar</Button>
          </Space>
        }
      >
        <Table
          rowKey="document_id"
          loading={loading}
          dataSource={filtered}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          columns={[
            {
              title: "Caso",
              dataIndex: "case_number",
              key: "case_number",
              render: (text: string, record: DocRow) => (
                <Button type="link" onClick={() => navigate(`/case/${record.case_id}`)}>
                  {text || `#${record.case_id}`}
                </Button>
              ),
            },
            {
              title: "Título",
              dataIndex: "title",
              key: "title",
              render: (text: string, record: DocRow) => (
                <a
                  href={`https://comarca.5bc.us/api/documents/download/${record.document_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {text}
                </a>
              ),
            },
            { title: "Tipo", dataIndex: "document_type", key: "document_type", width: 120 },
            {
              title: "Fecha",
              dataIndex: "upload_date",
              key: "upload_date",
              width: 120,
              render: (date: string) => (date ? dayjs(date).format("DD/MM/YYYY") : "—"),
            },
            {
              title: "Estado",
              dataIndex: "status",
              key: "status",
              width: 110,
              render: (status: string) => <Tag>{status || "—"}</Tag>,
            },
            {
              title: "OCR / DB1995",
              key: "ocr",
              width: 220,
              render: (_: unknown, record: DocRow) => {
                const chars = (record.content_text || "").trim().length;
                return (
                  <Space direction="vertical" size={0}>
                    <Tag color={chars > 0 ? "green" : "default"}>
                      {chars > 0 ? `${chars.toLocaleString()} chars` : "Sin OCR"}
                    </Tag>
                    <Button
                      type="link"
                      size="small"
                      icon={<FileTextOutlined />}
                      onClick={() => void openOcr(record)}
                    >
                      Ver OCR
                    </Button>
                  </Space>
                );
              },
            },
            {
              title: "Acciones",
              key: "actions",
              width: 120,
              render: (_: unknown, record: DocRow) => (
                <Button
                  type="link"
                  icon={<EyeOutlined />}
                  href={`https://comarca.5bc.us/api/documents/download/${record.document_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Ver
                </Button>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title={`OCR guardado — ${ocrTitle}`}
        open={ocrVisible}
        onCancel={() => setOcrVisible(false)}
        footer={null}
        width={900}
      >
        {ocrLoading ? (
          <Spin />
        ) : (
          <pre
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              maxHeight: "70vh",
              overflow: "auto",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              padding: 12,
              fontSize: 13,
              lineHeight: 1.45,
            }}
          >
            {ocrText}
          </pre>
        )}
      </Modal>
    </div>
  );
};

export default DocumentsPage;
